import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DataGridPro, GridActionsCellItem, koKR } from '@mui/x-data-grid-pro';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,  
  Switch,
  TextField,
  ToggleButtonGroup, 
  ToggleButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  FormInputDate,
  FormInputText,
} from "../form";
import  {
  Add,
  Clear,
  Delete,
  Edit,
  KeyboardDoubleArrowDown,
  KeyboardDoubleArrowUp,
  KeyboardArrowDown,
  KeyboardArrowUp,
  OpenInNew,
  Search,
  Settings,
  Visibility,
} from '@mui/icons-material';
import {
  ConfirmDialog,
} from "../dialog";
import {
  CustomNoRowsOverlay,
  // CustomLoadingOverlay,
} from "../datagrid";
import {
  StringAvatar,
} from "../avatar";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';  // 한국어 로케일 추가
import { dateFormat } from "../../utils";
import * as gclientActions from "../../store/gclient";
import GClientDialog from "./GClientDialog";
import GClientServiceDialog from "./GClientServiceDialog";
import * as dateUtils from '../../utils/dateUtils';
import { getGridHeight } from "../../constants/gridHeights";
import { PARTNERSHIP_TYPES, PARTNERSHIP_TYPE_LABELS } from "../../constants/gclientTypes";
import { MEMBERSHIP_TYPES, MEMBERSHIP_TYPE_LABELS } from "../../constants/gclientTypes";
import { uploadFilePath, fileServerUrl, mode } from '../../config';
import { filePreviewServerUrl } from '../../config';
import debounce from 'lodash/debounce';
import DocumentViewerDrawer from "../common/viewer/DocumentViewerDrawer";

const theme = createTheme();

const defaultValues = {
  searchName: "",
  partnerships: [''],
  memberships: [''],
  serviceDate: "",
  cmsDocumentPath: "",
  signYn: "",
  cmsYn: "",
  serviceMonth: "",
  startPaidEndDate: "",
  endPaidEndDate: "",
  };

const GClientManagement = ({ title, from }) => {

  const { handleSubmit, control, setValue, getValues } = useForm({ defaultValues });

  const [crudMode, setCrudMode] = useState('');
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removeId, setRemoveId] = useState(); // TODO : 파라미터 없으면 undefined?
  const [params, setParams] = useState({});
  const [pageSize, setPageSize] = useState(100);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [partnerships, setPartnerships] = useState(['']);
  const [memberships, setMemberships] = useState([MEMBERSHIP_TYPES[1]]);
  const [signYn, setSignYn] = useState("");
  const [cmsYn, setCmsYn] = useState("");
  //const [serviceMonth, setServiceMonth] = useState("");
  const [openServiceDialog, setOpenServiceDialog] = useState(false);
  // const [serviceMonthOptions, setServiceMonthOptions] = useState([
  //   { value: "", label: "선택" },  // 기본 "선택" 옵션 추가
  //   ...dateUtils.getServiceDateOptionsFromDate()
  // ]);
  const [documentViewerDrawerOpen, setDocumentViewerDrawerOpen] = useState(false);
  const [currentUrl, setCurrentUrl] = useState("");

  const searchInputRef = useRef("");

  // 현재 화면에 맞는 Grid 높이 계산
  const GRID_HEIGHT = getGridHeight('TWO_LINE_BUTTON_LAYER', 0); // 0px 추가 여백

  const handleSelect = async ({ type, params }) => {
    const { id } = params;

    setOpenBackdrop(true);

    if (type === "detail") {
      await select(id);
      setCrudMode('R');
      setOpen(true);
    } else if (type === "edit") {
      await select(id);
      setCrudMode('U');
      setOpen(true);
    } else if (type === "delete") {
      setRemoveId(id);
      setParams(params);
      setConfirmOpen(true);
    } else if (type === "service") {
      console.log("Params for Service:", params);
      setParams(params);
      setOpenServiceDialog(true);
    }

    setOpenBackdrop(false);
  }

  const handleSelectUpDown = async ({ type, id }) => {
    setOpenBackdrop(true);

    await reorder({ type, id });
    setLoaded(false);
    await selectAll();
    setTimeout(() => setLoaded(true), 300)

    setOpenBackdrop(false);
  }

  // Drag & Drop 순서 변경
  const handleRowOrderChange = async (params) => {
    
    const { oldIndex, targetIndex } = params;
    if (oldIndex === targetIndex) return;

    const newRows = [...rows];
    const [movedRow] = newRows.splice(oldIndex, 1);
    newRows.splice(targetIndex, 0, movedRow);

    // 즉시 UI 업데이트
    dispatch(gclientActions.setGClients(newRows));
    
    setOpenBackdrop(true);
    try {
      // 새로운 순서 정보를 생성
      const movedRow = rows[oldIndex];
      
      let result = await modifyRowOrder({
        id: movedRow.id,
        type: 'to',
        toOrder: targetIndex+1,
      });

      

    } catch (error) {
      console.error("Error row order:", error.serverResponse.message, error);
      // 에러 발생 시 원래 상태로 복구
      dispatch(gclientActions.setGClients(rows));
    }

    setOpenBackdrop(false);
  };

  const remove = (removeId) => {
    dispatch(gclientActions.remove(removeId))
    .then(res => selectAll())
    .catch(async (res) => {
      const data = await res.json();
      if (data && data.errors) setErrors(data.errors);
    });
  }

  // 저장/수정 다이얼로그
  const handleClickOpen = () => {
    initGClient();
    setOpen(true);
    setCrudMode('C');
  };

  const generateActions = (params) => {
    let arrActions = [
      <GridActionsCellItem
        icon={<OpenInNew />}
        label={"상세"}
        onClick={() => handleSelect({ type: 'detail', params })}
        showInMenu
      />,
      <GridActionsCellItem
        icon={<Edit />}
        label={"수정"}
        onClick={() => handleSelect({ type: 'edit', params })}
        showInMenu
      />,
      <GridActionsCellItem
        icon={<Delete />}
        label={"삭제"}
        onClick={() => handleSelect({ type: 'delete', params })}
        showInMenu
      />,
      <GridActionsCellItem
        icon={<Settings />}
        label={"서비스관리"}
        onClick={() => handleSelect({ type: 'service', params })}
        showInMenu
      />,
    ];

    return arrActions;
  }

  const handleChangeToggleRejectAlarmEmailYN = async ({ params, e }) => {
    e.stopPropagation();
    
    await modifyRejectAlarmEmailYN({ id: params.id, rejectAlarmEmailYN: e.target.checked ? 'Y' : 'N' });

    setLoaded(false);
    
    await selectAll();

    setTimeout(() => setLoaded(true), 300);
  }

  const handleClickViewCMSDocument = (documentPath, gclientId) => {
    let path = "";
    if (sessionUser.type === 'ADMIN' || sessionUser.id === gclientId) {
      const randNumber = Math.floor(Math.random()*99); // 캐싱으로 인해 이전 문서가 계속 보이는 문제 해결
      const encodedPath = encodeURIComponent(documentPath.replace(uploadFilePath, ''));
      path = `${fileServerUrl}${encodedPath}?q=cat&${randNumber}`;
    } else {
      path = `${filePreviewServerUrl}${encodeURIComponent(documentPath)}`;
    }
    // window.open(`${path}`, "미리보기", 'target="_self"');
    setCurrentUrl(path);
    if (!documentViewerDrawerOpen) {
      setDocumentViewerDrawerOpen(true);
    }
  }

  // CMS결제일자 변경버튼 액션
  const CmsPayDayCell = ({ value, row, id }) => {
    const [month, setMonth] = useState(value ? value.substring(0, 2) : '');
    const [day, setDay] = useState(value ? value.substring(2, 4) : '');
    const [isModified, setIsModified] = useState(false);
    
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Select
          value={month}
          onChange={(e) => {
            setMonth(e.target.value);
            setIsModified(true);
          }}
          size="small"
          sx={{ 
            width: 65,
            height: 25,
            '.MuiSelect-select': {
              padding: '2px 8px',
              fontSize: '0.75rem',
            }
          }}
        >
          {Array.from({ length: 12 }, (_, i) => (
            <MenuItem 
              key={i} 
              value={String(i + 1).padStart(2, '0')}
              sx={{ fontSize: '0.75rem' }}
            >
              {`${i + 1}월`}
            </MenuItem>
          ))}
        </Select>
        
        <Select
          value={day}
          onChange={(e) => {
            setDay(e.target.value);
            setIsModified(true);
          }}
          size="small"
          sx={{ 
            width: 65,
            height: 25,
            '.MuiSelect-select': {
              padding: '2px 8px',
              fontSize: '0.75rem',
            }
          }}
          disabled={!month}
        >
          {Array.from({ length: month ? new Date(2024, parseInt(month), 0).getDate() : 0 }, (_, i) => (
            <MenuItem 
              key={i} 
              value={String(i + 1).padStart(2, '0')}
              sx={{ fontSize: '0.75rem' }}
            >
              {`${i + 1}일`}
            </MenuItem>
          ))}
        </Select>
  
        <Button
          variant="contained"
          size="small"
          onClick={async () => {
            if (month && day) {
              try {
                await modifyCmsPayDay({ id, cmsPayDay: month + day });
                setIsModified(false);
                // 성공 시 목록 새로고침 필요하다면 추가
                // handleClickSearch();
              } catch (error) {
                console.error('Failed to update CMS pay day:', error);
              }
            }
          }}
          disabled={!isModified || !month || !day}
          sx={{ 
            minWidth: '35px',
            height: '25px',
            padding: '0 4px',
            fontSize: '0.75rem'
          }}
        >
          변경
        </Button>
      </Stack>
    );
  };

  // CMS결제금액 변경버튼 액션
  const CmsAmountCell = ({ value, row, id }) => {
    // 초기값에 콤마 추가
    const formatNumber = (num) => {
      if (!num) return '';
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };
    
    const [amount, setAmount] = useState(value ? formatNumber(value) : '');
    const [isModified, setIsModified] = useState(false);
  
    const handleAmountChange = (e) => {
      // 숫자와 콤마만 허용
      const newValue = e.target.value.replace(/[^\d,]/g, '');
      // 콤마 제거 후 숫자만 추출
      const numericValue = newValue.replace(/,/g, '');
      
      if (numericValue) {
        // 천단위 콤마 추가
        const formattedValue = Number(numericValue).toLocaleString();
        setAmount(formattedValue);
        setIsModified(true);
      } else {
        setAmount('');
        setIsModified(true);
      }
    };
  
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <TextField
          value={amount}
          onChange={handleAmountChange}
          size="small"
          sx={{ 
            width: 80,
            '& .MuiInputBase-input': {
              padding: '2px 8px',
              fontSize: '0.75rem',
              height: '21px',
            }
          }}
          inputProps={{
            style: { textAlign: 'right' }
          }}
        />
  
        <Button
          variant="contained"
          size="small"
          onClick={async () => {
            if (amount) {
              try {
                // API 호출 시 콤마 제거하고 숫자만 전송
                const numericAmount = amount.replace(/,/g, '');
                await modifyCmsAmount({ id, cmsAmount: numericAmount });
                setIsModified(false);
              } catch (error) {
                console.error('Failed to update CMS amount:', error);
              }
            }
          }}
          disabled={!isModified || !amount}
          sx={{ 
            minWidth: '35px',
            height: '25px',
            padding: '0 4px',
            fontSize: '0.75rem'
          }}
        >
          변경
        </Button>
      </Stack>
    );
  };

  const columnsBasic = [
    {
      field: 'name',
      headerName: '거래선명',
      width: 220,
    },
    {
      field: 'gclientTypes',
      headerName: '대분류',
      width: 150,
      headerAlign: 'center',
      renderCell: (params) => {
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
            {params.row?.gclientTypes?.map((gclientType, idx) => {
              return (
                <StringAvatar
                  name={gclientType.displayType ? gclientType.displayType : ""}
                  width={20}
                  height={20}
                  fontSize={"small"}
                  color={gclientType.color}
                />
              );
            })}
          </Box>
        )
      }
    },
    {
      field: 'gclientType04docus',
      headerName: '세분류',
      width: 150,
      headerAlign: 'center',
      renderCell: (params) => {
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
            {params.row?.gclientType04docus?.map((gclientType04docu, idx) => {
              return (
                <StringAvatar
                  name={gclientType04docu?.displayType ? gclientType04docu.displayType : ""}
                  width={20}
                  height={20}
                  fontSize={"small"}
                  color={gclientType04docu?.color}
                />
              );
            })}
          </Box>
        )
      }
    },
    {
      field: 'partnership',
      headerName: '파트너쉽',
      width: 100,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'membership',
      headerName: '멤버쉽',
      width: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        return (
          MEMBERSHIP_TYPE_LABELS[params.value]
        )
      }
    },
    {
      field: 'joinDate',
      headerName: '최초 가입일',
      width: 160,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => params.value ? dateFormat(params.value) : '',
    },
    {
      field: 'serviceDate',
      headerName: '서비스 시작일',
      width: 160,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => params.value ? dateFormat(params.value) : '',
    },
    {
      field: 'paidEndDate',
      headerName: '서비스 종료일',
      width: 160,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => params.value ? dateFormat(params.value) : '',
    },
    {
      field: 'cmsDocumentPath',
      headerName: 'CMS 신청서',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            {params.row.cmsDocumentPath && (
              <Tooltip title={"CMS 신청서 보기"} placement="right-start">
                <IconButton
                  aria-label="view"
                  edge="end"
                  onClick={() => handleClickViewCMSDocument(params.row.cmsDocumentPath, params.id)}
                  sx={{ color: "#1976D2" }}
                >
                  <Visibility />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      }
    },
    {
      field: 'cmsPayDay',
      headerName: 'CMS결제일자',
      width: 220,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <CmsPayDayCell 
          value={params.row.cmsPayDay} 
          row={params.row} 
          id={params.id} 
        />
      )
    },
    {
      field: 'cmsAmount',
      headerName: 'CMS결제금액',
      width: 160,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <CmsAmountCell 
          value={params.row.cmsAmount} 
          row={params.row} 
          id={params.id}
        />
      )
    },
    {
      field: 'bizRegNumber',
      headerName: '사업자등록번호',
      width: 160,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'code',
      headerName: '코드',
      width: 100,
      hide: true,
    },
    {
      field: 'phone',
      headerName: '연락처',
      width: 120,
    },
    {
      field: 'address',
      headerName: '주소',
      width: 400,
    },
    {
      field: 'email',
      headerName: '회사대표메일',
      width: 200,
    },
    // {
    //   field: 'inChargeName',
    //   headerName: '담당자명',
    //   width: 120,
    //   valueGetter: (params) => {
    //     const { inCharges } = params.row;
    //     if (inCharges && Array.isArray(inCharges) && inCharges.length > 0) {
    //       const additional = inCharges.length > 1 ? ` 외 ${inCharges.length-1}인` : ""
    //       return `${inCharges[0].inChargeName}${additional}`;
    //     }

    //     return "";
    //   },
    // },
    // {
    //   field: 'inChargePhone',
    //   headerName: '담당자연락처',
    //   width: 150,
    //   valueGetter: (params) => {
    //     const { inCharges } = params.row;
    //     if (inCharges && Array.isArray(inCharges) && inCharges.length > 0) {
    //       return inCharges[0].inChargePhone;
    //     }

    //     return "";
    //   },
    // },
    // {
    //   field: 'inChargeEmail',
    //   headerName: '담당자메일',
    //   width: 220,
    //   valueGetter: (params) => {
    //     const { inCharges } = params.row;
    //     if (inCharges && Array.isArray(inCharges) && inCharges.length > 0) {
    //       return inCharges[0].inChargeEmail;
    //     }

    //     return "";
    //   },
    // },
    {
      field: 'rejectAlarmEmailYN',
      headerName: '만료안내메일 수신거부',
      headerAlign: 'center',
      align: 'center',
      width: 200,
      renderCell: (params) => {
        let rejectAlarmEmailYN = params.value;
        return (
          <Switch
            checked={rejectAlarmEmailYN === 'Y' ? true : false}
            onChange={(e) => handleChangeToggleRejectAlarmEmailYN({ params, e })}
          />
        )
      },
    },
    {
      field: 'systemUrl',
      headerName: '시스템 URL',
      width: 280,
    },
    {
      field: 'comments',
      headerName: '설명',
      width: 280,
    },
    {
      field: 'createdAt',
      headerName: '생성일시',
      width: 160,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => dateFormat(params.value),
    },
    {
      field: 'updatedAt',
      headerName: '수정일시',
      width: 160,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => dateFormat(params.value),
    },
  ];

  const columns = (from !== undefined && from !== null) ? columnsBasic : columnsBasic.concat({
    field: 'actions',
    headerName: <Tooltip title={"상세/수정/삭제"} followCursor><Box>{"기능"}</Box></Tooltip>,
    width: 70,
    type: 'actions',
    getActions: (params) => generateActions(params),
  });

  const sessionUser = useSelector(state => state.session.sessionUser);

  const rows = useSelector((state) => state.gclient.gclients);
  const selectedRow = useSelector((state) => state.gclient.gclient);
  const gclientTypes = useSelector((state) => state.gclientType.gclientTypes);
  
  // 데이터 관리
  const dispatch = useDispatch();

  const selectAll = () => dispatch(gclientActions.selectAll())
  const selectAllByIds = (ids) => dispatch(gclientActions.selectAllByIds(ids))
  const selectAllByParams = (params) => dispatch(gclientActions.selectAllByParams(params))
  const select = (id) => dispatch(gclientActions.select(id))
  const initGClient = () => dispatch(gclientActions.init())
  const reorder = ({ type, id }) => gclientActions.reorder({ type, id })
  const modifyRowOrder = ({ id, type, toOrder }) => gclientActions.modifyRowOrder({ id, type, toOrder })
  const modifyRejectAlarmEmailYN = ({ id, rejectAlarmEmailYN }) => dispatch(gclientActions.modifyRejectAlarmEmailYN({ id, rejectAlarmEmailYN }))
  const modifyCmsPayDay = ({ id, cmsPayDay }) => gclientActions.modifyCmsPayDay({ id, cmsPayDay })
  const modifyCmsAmount = ({ id, cmsAmount }) => gclientActions.modifyCmsAmount({ id, cmsAmount })

  // debounce된 검색 함수 생성
  const debouncedSearch = useCallback(
    debounce((searchName) => {
      setValue("searchName", searchName);
      handleClickSearch();
    }, 500),  // 500ms 딜레이
    [] // 컴포넌트가 마운트될 때만 생성
  );

  const handleChangeSearchName = useCallback((e) => {
    const searchName = e.target.value;
    setValue("searchName", searchName);
   
    debouncedSearch(searchName);
  }, [debouncedSearch]);

  // 컴포넌트 언마운트 시 debounce 취소
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // 검색 버튼 클릭 시 호출
  const handleClickSearch = () => {
    const params = {
      signYn: getValues("signYn"),
      cmsYn: getValues("cmsYn"),
      name: getValues("searchName"),
      startPaidEndDate: null,
      endPaidEndDate: null
    };

    // 파트너쉽
    const currentPartnerships = getValues("partnerships");
    if (currentPartnerships?.length > 0 && !currentPartnerships.includes("")) {
      params.partnerships = [...currentPartnerships];
    }
    //console.log("params.partnerships:", params.partnerships);
    
    // 멤버쉽
    const currentMemberships = getValues("memberships");
    if (currentMemberships?.length > 0 && !currentMemberships.includes("")) {
      params.memberships = [...currentMemberships];
    }
    //console.log("params.memberships:", params.memberships);

    if (getValues("serviceMonth") !== "") {
      const dateRange = dateUtils.getPaidEndDateRange(getValues("serviceMonth"));
      params.startPaidEndDate = dateRange.startPaidEndDate;
      params.endPaidEndDate = dateRange.endPaidEndDate;
    }
    
    console.log("Search Params:", params);
    
    selectAllByParams(params);
  };

  // 파트너쉽 변경 핸들러
  const handlePartnershipChange = (event, newPartnership) => {

    const clickedValue = event.target.value;
    const isPressed = event.target.getAttribute('aria-pressed') === 'true';
    //console.log('clickedValue : ', clickedValue, 'isPressed : ', isPressed);
    //console.log('newPartnership : ', newPartnership, newPartnership.length);
    
    if (newPartnership.length === 0) {
      return;
    }
    
    let updatedPartnerships;
    if (clickedValue === '') {
      updatedPartnerships = [''];  // 전체만 활성화, 나머지 모두 비활성화
    } else {  
      // 전체가 아닌 경우
      updatedPartnerships = newPartnership.filter(value => value !== '');
    }

    console.log('updatedPartnerships : ', updatedPartnerships);

    setPartnerships(() => updatedPartnerships);
    setValue("partnerships", updatedPartnerships);
    
    handleClickSearch();
  };

  const handleMembershipChange = (event, newMembership) => {
    const clickedValue = event.target.value;
    const isPressed = event.target.getAttribute('aria-pressed') === 'true';
    console.log('clickedValue : ', clickedValue, 'isPressed : ', isPressed);
    console.log('newMembership : ', newMembership, newMembership.length);
    if (newMembership.length === 0) {
      return;
    }
    let updatedMemberships;
    if (clickedValue === '') {
      updatedMemberships = [''];
    } else {
      updatedMemberships = newMembership.filter(value => value !== "");
    }

    setMemberships(() => updatedMemberships);
    setValue("memberships", updatedMemberships);

    handleClickSearch();
  };

  // 서비스 종료월 변경 시 호출
  // const handleChangeServiceMonth = (e) => {
  //   let value;
  
  //   if (e?.target) {
  //     value = e.target.value;
  //   } else {
  //     value = e || "";  // e가 직접 값이거나 없는 경우
  //   }
  
  //   console.log("New service date:", value);
  //   //setServiceMonth(newDate);
  //   // form 값 업데이트
  //   setValue("serviceMonth", value);

  //   // 서비스 종료월 옵션 업데이트
  //   if (value !== "") {
  //     const options = [
  //       { value: "", label: "선택" },
  //       ...dateUtils.getServiceDateOptionsFromDate(value)
  //     ];
  //     setServiceMonthOptions(options);
  //   }

  //   handleClickSearch();
  // };

  // 월 선택 시 호출되는 함수 수정
  const handleChangeServiceMonth = (newDate) => {
    // null 체크 (사용자가 날짜를 지울 때)
    if (!newDate) {
      setValue("serviceMonth", "");
      handleClickSearch();
      return;
    }
    // YYYY-MM 형식으로 변환
    const formattedDate = dayjs(newDate).format('YYYY-MM');
    console.log("New service month:", formattedDate);
  
    setValue("serviceMonth", formattedDate);
    handleClickSearch();
  };

  const handleRadioChange = (type, e) => {
    const value = e.target.value === "" ? "" : e.target.value === "true";
    
    if (type === 'signYn') {
      setSignYn(value);
      setValue("signYn", value);
    } else if (type === 'cmsYn') {
      setCmsYn(value);
      setValue("cmsYn", value);
    }
    
    handleClickSearch(); 
  };

  useEffect(
    async () => {
      if (from?.params?.selManufacturers) {
        const ids = from.params.selManufacturers.map(manufacturer => ({ id: manufacturer.id }));
        console.log(ids)
        await selectAllByIds(ids);
      } else {
        await selectAll();
      }
      
      setLoaded(true);
    }, [dispatch]
  );

  

  return (
    <ThemeProvider theme={theme}>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackdrop}
        // onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container component="main" maxWidth="false">
        <CssBaseline />
        <GClientDialog
          crudMode={crudMode}
          setCrudMode={setCrudMode}
          open={open}
          setOpen={setOpen}
          selectedRow={selectedRow}
          refresh={selectAll}
        />
        <GClientServiceDialog
          open={openServiceDialog}
          setOpen={setOpenServiceDialog}
          params={params}
        />
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={10}>
              <Typography variant="h5">
                {title}
              </Typography>
            </Grid>
            {
              (from === undefined || from === null) && (
                <>
                  <Grid item xs={12} sm={10}>
                    <Stack direction="row" spacing={1}>
                      <FormInputText
                        name="searchName"
                        control={control}
                        label={"거래선명 또는 사업자번호로 검색하세요."}
                        onCustomChange={handleChangeSearchName}
                        inputRef={searchInputRef}
                        sx={{
                          width: 250,
                          '& .MuiFormLabel-root' : {
                            fontSize: '0.85rem',
                          },
                          input: { fontSize: '0.85rem' }
                        }}
                      />
                      <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                        <Typography style={{ fontSize: '1rem', color: 'rgba(0, 0, 0, 1)', marginLeft: '40px' }}>
                          파트너쉽
                        </Typography>
                        <ToggleButtonGroup
                          value={partnerships}
                          onChange={handlePartnershipChange}
                          aria-label="파트너쉽"
                          multiple
                          size="small"
                          color="primary"
                          sx={{ height: 37 }}
                        >
                          <ToggleButton value="" aria-label="전체" color="primary">
                            전체
                          </ToggleButton>
                          {PARTNERSHIP_TYPES.map((type) => (
                            <ToggleButton key={type} value={type} aria-label={type} color="primary">
                              {PARTNERSHIP_TYPE_LABELS[type]}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </FormControl>
                      <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                        <Typography style={{ fontSize: '1rem', color: 'rgba(0, 0, 0, 1)', marginLeft: '40px' }}>
                          멤버쉽
                        </Typography>
                        <ToggleButtonGroup
                          value={memberships}
                          onChange={handleMembershipChange}
                          aria-label="멤버쉽"
                          multiple
                          size="small"
                          color="primary"
                          sx={{ height: 37 }}
                        >
                          <ToggleButton value="" aria-label="전체" color="primary">
                            전체
                          </ToggleButton>
                          {MEMBERSHIP_TYPES.map((type) => (
                            <ToggleButton key={type} value={type} aria-label={type} color="primary">
                              {MEMBERSHIP_TYPE_LABELS[type]}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </FormControl>
                      <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                      <Typography style={{ fontSize: '1rem', color: 'rgba(0, 0, 0, 1)', marginLeft: '40px' }}>
                        서비스 종료월
                      </Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
                      <DatePicker
                        value={getValues("serviceMonth") ? dayjs(getValues("serviceMonth")) : null}
                        onChange={handleChangeServiceMonth}
                        views={['year', 'month']}
                        inputFormat="YYYY-MM"
                        minDate={dayjs('2024-01-01')}
                        maxDate={dayjs('2035-12-31')}
                        clearable
                        // onClear={() => {
                        //   setValue("serviceMonth", "");
                        //   handleClickSearch();  // X 버튼 클릭 시 즉시 조회
                        // }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <React.Fragment>
                                  {getValues("serviceMonth") && (
                                    <IconButton
                                      onClick={() => {
                                        setValue("serviceMonth", "");
                                        handleClickSearch();  // X 버튼 클릭 시 즉시 조회
                                      }}
                                      size="small"
                                    >
                                      <Clear fontSize="small" />
                                    </IconButton>
                                  )}
                                  {params.InputProps.endAdornment}
                                </React.Fragment>
                              ),
                            }}
                            sx={{
                              width: 200,
                              '& .MuiInputBase-input': {
                                fontSize: '0.85rem',
                                padding: '8.5px 14px',
                              },
                              '& .MuiInputBase-root': {
                                paddingRight: '8px',
                              },
                              '& .MuiInputAdornment-root': {
                                height: 'auto',
                                maxHeight: 'none',
                                position: 'relative',
                                visibility: 'visible',
                                display: 'flex',
                                marginLeft: '5px',
                              },
                              '& .MuiIconButton-root': {
                                padding: '4px',
                                visibility: 'visible',
                                marginRight: '0px',
                              }
                            }}
                          />
                        )}
                      />
                      </LocalizationProvider>
                      </FormControl>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={10}>
                    <Stack direction="row" spacing={1}>
                      <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                        <Typography style={{ fontSize: '1rem', color: 'rgba(0, 0, 0, 1)'}}>
                          직인등록여부
                        </Typography>
                        <RadioGroup
                          row
                          name="signYn"
                          value={signYn}
                          defaultValue="" 
                          onChange={(e) => handleRadioChange('signYn', e)}
                        >
                          <FormControlLabel 
                            value="" 
                            control={<Radio />} 
                            label="전체" 
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                          />
                          <FormControlLabel 
                            value="true"
                            control={<Radio />} 
                            label="등록" 
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                          />
                          <FormControlLabel 
                            value="false" 
                            control={<Radio />} 
                            label="미등록" 
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                          />
                        </RadioGroup>
                      </FormControl>
                      <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                        <Typography style={{ fontSize: '1rem', color: 'rgba(0, 0, 0, 1)', marginLeft: '40px' }}>
                          CMS등록여부
                        </Typography>
                        <RadioGroup
                          row
                          name="cmsYn"
                          value={cmsYn}
                          defaultValue=""
                          onChange={(e) => handleRadioChange('cmsYn', e)}
                        >
                          <FormControlLabel 
                            value=""
                            control={<Radio />} 
                            label="전체" 
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                          />
                          <FormControlLabel 
                            value={true} 
                            control={<Radio />} 
                            label="등록" 
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                          />
                          <FormControlLabel 
                            value={false} 
                            control={<Radio />} 
                            label="미등록" 
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                          />
                        </RadioGroup>
                      </FormControl>
                      
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={2} display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      startIcon={<Search />}
                      onClick={handleClickSearch}
                      sx={{ ml: 5, mr: 1 }}
                    >
                      {"검색"}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={handleClickOpen}
                    >
                      {"등록하기"}
                    </Button>
                  </Grid>
                </>
              )
            }

          </Grid>
          <div style={{ height: GRID_HEIGHT.FULL, width: '100%', marginTop: 10 }}>
            <DataGridPro
              localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
              sx={{ cursor: 'pointer', fontSize: '0.85em' }}
              initialState={{ pinnedColumns: { right: ['actions'] } }}
              slots={{
                noRowsOverlay: CustomNoRowsOverlay,
                loadingOverlay: LinearProgress,
              }}
              rowReordering
              onRowOrderChange={handleRowOrderChange}
              headerHeight={38}
              rowHeight={34}
              loading={!loaded}
              rows={rows}
              columns={columns}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[10, 20, 50, 100]}
              pagination
              onRowDoubleClick={(params) => handleSelect({ type: 'edit', params })}
            />
          </div>
        </Box>
        <ConfirmDialog
          removeId={removeId}
          title={"삭제"}
          open={confirmOpen}
          setOpen={setConfirmOpen}
          onConfirm={remove}
          onCancel={() => {}}
        >
          {`"${params && params.row && params.row.name || ""}(아이디 : ${params && params.id  || ""})"를 삭제하시겠습니까?`}
        </ConfirmDialog>
        <DocumentViewerDrawer 
          open={documentViewerDrawerOpen}
          onClose={() => setDocumentViewerDrawerOpen(false)}
          url={currentUrl}
          anchor="left"
        />
      </Container>
    </ThemeProvider>
  );
};

export default GClientManagement;
